import { Action } from 'redux';
import * as _ from 'underscore';

import { SiteUrls } from '@tableau/shared-urls';
import { OrderedCreateContentActions } from '../../app/spaces/Explorer/CreateContent/CreateContentActionsList';
import { TopLevelProjectId } from '../../app/state/SpaceInfo';
import { Messages } from '../../codegen/Localize';
import { ExploreContentApi_ContentTypeValue } from '../../ts/ServerApi/types/IContent';
import { HelpService } from '../../ts/services/HelpService';
import { ReactModalService } from '../../ts/services/ReactModalService';
import { UploadWorkbookDialogOpts } from '../../uploadContent/components/ConnectedUploadWorkbookDialog';
import { sendUploadWorkbookEntryPointTelemetry } from '../../uploadContent/helpers/UploadWorkbookTelemetryEvents';

// Used so we can identify action types in telemetry
export enum WelcomeChannelActions {
  createWorkbook = 'createWorkbook',
  uploadWorkbook = 'uploadWorkbook',
  startExploring = 'startExploring',
  downloadDesktop = 'downloadDesktop',
  downloadPrep = 'downloadPrep',
  manageProjects = 'manageProjects',
  manageUsers = 'manageUsers',
  dismiss = 'dismiss',
}

export type WelcomeChannelLinkDetails = {
  actionName: WelcomeChannelActions;
  linkTitle: string;
  openNewTab: boolean;
  url?: string;
  getAction?: () => Action;  // Redux action
  onClick?: () => void;
};

// This is a class and not a namespace because it depends on the Localize object to be loaded beforehand.
// Using a namespace would try to initialize the object and call Messages before it was ready.
export class WelcomeChannelLinks {
  private siteUrls: SiteUrls;
  private locale: string;
  private queryParams: string;

  constructor(siteUrls: SiteUrls, helpService: HelpService) {
    this.siteUrls = siteUrls;
    this.locale = helpService.helpUrlLanguage();
    this.queryParams = helpService.getQueryParams();
  }

  createWorkbook(): WelcomeChannelLinkDetails {
    return {
      actionName: WelcomeChannelActions.createWorkbook,
      linkTitle: Messages.home_action_createWorkbook(),
      openNewTab: false,
      getAction: () => {
        const workbookContentType = ExploreContentApi_ContentTypeValue.workbook;
        const createWorkbookAction = _.find(OrderedCreateContentActions, a => a.contentType === workbookContentType);

        return createWorkbookAction.createReduxAction(TopLevelProjectId);
      }
    };
  }

  uploadWorkbook(): WelcomeChannelLinkDetails {
    return {
      actionName: WelcomeChannelActions.uploadWorkbook,
      linkTitle: Messages.contentUpload_uploadWorkbook(),
      openNewTab: false,
      onClick: () => {
        ReactModalService.open(UploadWorkbookDialogOpts());
        sendUploadWorkbookEntryPointTelemetry('welcome-banner');
      }
    };
  }

  startExploring(): WelcomeChannelLinkDetails {
    return {
      actionName: WelcomeChannelActions.startExploring,
      linkTitle: Messages.home_welcomeChannel_ActionStartExploring(),
      openNewTab: false,
      url: this.siteUrls.exploreUrl(),
    };
  }

  downloadDesktop(): WelcomeChannelLinkDetails {
    return {
      actionName: WelcomeChannelActions.downloadDesktop,
      linkTitle: Messages.home_welcomeChannel_ActionDownloadDesktop(),
      openNewTab: true,
      url: 'https://www.tableau.com/' + this.locale + '/app/server-home-desktop?' + this.queryParams,
    };
  }

  downloadPrep(): WelcomeChannelLinkDetails {
    return {
      actionName: WelcomeChannelActions.downloadPrep,
      linkTitle: Messages.home_welcomeChannel_ActionDownloadPrep(),
      openNewTab: true,
      url: 'https://www.tableau.com/' + this.locale + '/app/server-home-prep?' + this.queryParams,
    };
  }

  manageProjects(): WelcomeChannelLinkDetails{
    return {
      actionName: WelcomeChannelActions.manageProjects,
      linkTitle: Messages.home_welcomeChannel_ActionManageProjects(),
      openNewTab: false,
      url: this.siteUrls.exploreUrl(),  // We want to drop them to the top-level projects, which is /explore
    };
  }

  manageUsers(): WelcomeChannelLinkDetails {
    return {
      actionName: WelcomeChannelActions.manageUsers,
      linkTitle: Messages.home_welcomeChannel_ActionManageUsers(),
      openNewTab: false,
      url: this.siteUrls.usersUrl(),
    };
  }
}
